/* eslint-disable jsx-a11y/anchor-is-valid */
import React from "react";
import Alert from "../Others/Alert";
import { Pagination } from "@/components/ui/pagination";
import { cn } from "@/lib/utils";
import { inputClass, selectClass } from "@/lib/formStyles";

const formatNumber = (value = 0) =>
  new Intl.NumberFormat("fr-FR").format(value || 0);

const TabTwo = ({
  drugsSuivie,
  handleChange,
  drugsAll,
  form,
  handleStockSubmit,
  errors,
  stockSearch,
  setStockSearch,
  stockPagination,
  onStockPageChange,
  stockFetching,
}) => {
  const {
    totalElements = 0,
    totalPages = 0,
    currentPage = 0,
    hasNext = false,
    hasPrevious = false,
  } = stockPagination || {};

  return (
    <div className="space-y-8">
      {/* Hero */}
      <section className="animate-in fade-in slide-in-from-bottom-2 duration-500 fill-mode-both delay-100 rounded-[28px] border border-slate-200 bg-white shadow-sm p-6 md:p-8">
        <div className="space-y-3">
          <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.32em] text-emerald-700 font-semibold">
            <i className="bx bx-trending-up text-base"></i>
            Mouvement de stock
          </span>
          <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 leading-tight">
            Suivi des ventes
          </h1>
          <p className="text-sm text-slate-500 max-w-2xl">
            Enregistrez chaque sortie de médicament et conservez un journal
            chronologique des ventes par vendeur.
          </p>
        </div>

        {errors?.length > 0 && (
          <div className="mt-6 space-y-2">
            {errors.map((error, idx) => (
              <Alert key={idx} message={error.message} />
            ))}
          </div>
        )}

        <form className="mt-8 grid grid-cols-1 lg:grid-cols-[1.4fr_1fr_auto] gap-3 items-end">
          <label className="block">
            <span className="block text-sm font-medium text-slate-600 mb-1">
              Médicament
            </span>
            <div className="relative">
              <select
                name="drugsId"
                value={form.drugsId || ""}
                onChange={handleChange}
                className={cn(selectClass, "mt-0")}
              >
                <option value="">Sélectionner un médicament…</option>
                {(drugsAll || []).map((drug) => (
                  <option key={drug.id} value={drug.id}>
                    {drug.name}
                  </option>
                ))}
              </select>
              <i className="bx bx-chevron-down absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-lg pointer-events-none" />
            </div>
          </label>
          <label className="block">
            <span className="block text-sm font-medium text-slate-600 mb-1">
              Quantité vendue
            </span>
            <input
              type="number"
              name="quantity"
              min="1"
              value={form.quantity}
              onChange={handleChange}
              className={cn(inputClass, "mt-0")}
            />
          </label>
          <button
            type="button"
            onClick={handleStockSubmit}
            className="group/cta relative inline-flex items-center justify-center gap-2 overflow-hidden rounded-xl bg-green-600 px-5 h-11 text-sm font-medium text-white shadow-lg shadow-green-600/25 transition-all duration-300 hover:bg-green-700 active:bg-green-800"
          >
            <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent transition-transform duration-700 group-hover/cta:translate-x-full" />
            <i className="bx bx-plus relative z-10 text-base"></i>
            <span className="relative z-10">Enregistrer la vente</span>
          </button>
        </form>
      </section>

      {/* Search — serveur, debounce 300 ms côté parent */}
      <section className="animate-in fade-in slide-in-from-bottom-2 duration-500 fill-mode-both delay-200">
        <div className="relative w-full max-w-md">
          <i className="bx bx-search absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-lg pointer-events-none"></i>
          <input
            type="text"
            value={stockSearch}
            onChange={(e) => setStockSearch(e.target.value)}
            placeholder="Rechercher par médicament ou vendeur…"
            className={cn(inputClass, "mt-0 pl-11")}
          />
        </div>
      </section>

      {/* Table */}
      <section className="animate-in fade-in slide-in-from-bottom-2 duration-500 fill-mode-both delay-300 rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50/70 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                <th className="px-4 py-3 text-left">Date</th>
                <th className="px-4 py-3 text-left">Médicament</th>
                <th className="px-4 py-3 text-left">Vendeur</th>
                <th className="px-4 py-3 text-right">Quantité</th>
                <th className="px-4 py-3 text-right">Prix unitaire</th>
                <th className="px-4 py-3 text-right pr-6">Total</th>
              </tr>
            </thead>
            <tbody>
              {drugsSuivie.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-16">
                    <div className="flex flex-col items-center justify-center text-center">
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100 mb-3">
                        <i className="bx bx-receipt text-2xl"></i>
                      </div>
                      <p className="text-sm font-medium text-slate-700">
                        Aucune vente enregistrée
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        Les ventes apparaîtront ici dès leur saisie.
                      </p>
                    </div>
                  </td>
                </tr>
              )}

              {drugsSuivie.map((drug, idx) => (
                <tr
                  key={drug?.id ?? idx}
                  className="group/row border-t border-slate-100 hover:bg-emerald-50/40 transition-colors"
                >
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-500">
                    {drug?.createdDate || "—"}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100">
                        <i className="bx bx-capsule text-sm"></i>
                      </div>
                      <span className="text-sm font-medium text-slate-800">
                        {drug?.drugs?.name || "—"}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-600">
                    {drug?.user?.userName || drug?.user?.name || "—"}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium text-slate-800">
                    {drug?.quantity}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-right text-sm text-slate-600">
                    {formatNumber(drug?.drugs?.basePrice)}{" "}
                    <span className="text-xs text-slate-400">FCFA</span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-semibold text-emerald-600 pr-6">
                    {formatNumber(drug?.total)}{" "}
                    <span className="text-xs text-emerald-500/70 font-normal">
                      FCFA
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalElements={totalElements}
        currentCount={drugsSuivie.length}
        hasNext={hasNext}
        hasPrevious={hasPrevious}
        onPageChange={onStockPageChange}
        itemLabel="vente"
        isLoading={stockFetching}
      />
    </div>
  );
};

export default TabTwo;
